import { useState, useCallback, useEffect } from 'react';
import { PAYMENT_CONFIG } from '../config/payment';
import api from '../services/api';

export function usePaymentForm({ onSuccess, onError } = {}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);

  const loadMasterCardScript = useCallback(async () => {
    try {
      const existingScript = document.getElementById(PAYMENT_CONFIG.SCRIPT_ID);
      if (existingScript) {
        existingScript.remove();
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = PAYMENT_CONFIG.SCRIPT_ID;
        script.src = PAYMENT_CONFIG.SCRIPT_URL;
        script.async = true;

        script.onload = () => {
          setTimeout(resolve, 100);
        };
        script.onerror = () => reject(new Error('Failed to load payment script'));

        document.head.appendChild(script);
      });
    } catch (error) {
      throw new Error('Failed to load payment script');
    }
  }, []);

  const initializePayment = useCallback(async (amount) => {
    setIsProcessing(true);
    setError('');

    try {
      await loadMasterCardScript();
      const response = await api.post('/initiatePayment', { amount });

      if (!response.data?.sessionId) {
        throw new Error('Invalid response from server');
      }

      setSessionId(response.data.sessionId);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to initialize payment';
      setError(errorMessage);
      onError?.(new Error(errorMessage));
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, [loadMasterCardScript, onError]);

  const finalizePayment = useCallback(async () => {
    if (!sessionId) {
      throw new Error('No active payment session');
    }

    setIsProcessing(true);
    setError('');

    try {
      const browserDetails = {
        "3DSecureChallengeWindowSize": "FULL_SCREEN",
        "acceptHeaders": navigator.userAgent,
        "colorDepth": window.screen.colorDepth,
        "javaEnabled": false,
        "language": navigator.language || "en-US",
        "screenHeight": window.screen.height,
        "screenWidth": window.screen.width,
        "timeZone": new Date().getTimezoneOffset()
      };

      const response = await api.post('/finalize', { sessionId, browserDetails });
      
      // Only call onSuccess if payment is successful and doesn't need 3D auth
      if (response.data.message === 'successful') {
        onSuccess?.();
      }
      
      return response;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process payment';
      setError(errorMessage);
      onError?.(new Error(errorMessage));
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, [sessionId, onSuccess, onError]);

  const cleanup = useCallback(() => {
    const script = document.getElementById(PAYMENT_CONFIG.SCRIPT_ID);
    if (script) {
      script.remove();
    }
    setSessionId(null);
    setError('');
    setIsProcessing(false);
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isProcessing,
    error,
    sessionId,
    initializePayment,
    finalizePayment,
    cleanup
  };
}